import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import nvCookieHelpers from '../../../services/cookieService';
import { useHeaderMenu } from '../../../contexts/login.context';
import useLoader from '../../../contexts/LoaderContext';
import * as intrfce from '../Interfaces';

interface UseCartSuccessProps {
  currencySign: string;
  locale: string;
  countyField: boolean;
  userType: string | undefined;
  savedBillingAddress: intrfce.iSavedAddress[];
  savedShippingAddress: intrfce.iSavedAddress[];
  t: (key: string) => string;
  setBrand: (brand: string) => void;
  setFormTaxData: (data: intrfce.iTaxFormData) => void;
  setIsBusinessPurchase: (value: boolean) => void;
  setIsShipping: (value: boolean) => void;
  setCartData: (data: any) => void;
  setShippingList: (list: any[]) => void;
  setShippingOptions: (options: intrfce.iShippingDetails) => void;
  setSavedBillingAddress: (addresses: intrfce.iSavedAddress[]) => void;
  setSavedShippingAddress: (addresses: intrfce.iSavedAddress[]) => void;
  setFormBillingData: (data: intrfce.iBillingFormData | ((prev: intrfce.iBillingFormData) => intrfce.iBillingFormData)) => void;
  setFormBillingAddress: (address: intrfce.iAddressFormData) => void;
  setSelectedBillingAddress: (address: intrfce.iSavedAddress | null | "reset" | ((prev: intrfce.iSavedAddress | null | "reset") => intrfce.iSavedAddress | null | "reset")) => void;
  setIsBillingReadonly: (value: boolean) => void;
  setFormData: (data: intrfce.iFormContainer) => void;
  setSelectedShippingAddress: (address: intrfce.iSavedAddress | null | "reset" | ((prev: intrfce.iSavedAddress | null | "reset") => intrfce.iSavedAddress | null | "reset")) => void;
  setIsShippingReadonly: (value: boolean) => void;
  formFieldsRef: React.MutableRefObject<any>;
  getStateValue: (value: any, fallback: any) => any;
  emptyCart: (message: string, pid?: number) => void;
  orderAccount: () => void;
}

export const useCartSuccess = (props: UseCartSuccessProps) => {
  const {
    currencySign,
    locale,
    countyField,
    userType,
    savedBillingAddress,
    savedShippingAddress,
    t,
    setBrand,
    setFormTaxData,
    setIsBusinessPurchase,
    setIsShipping,
    setCartData,
    setShippingList,
    setShippingOptions,
    setSavedBillingAddress,
    setSavedShippingAddress,
    setFormBillingData,
    setFormBillingAddress,
    setSelectedBillingAddress,
    setIsBillingReadonly,
    setFormData,
    setSelectedShippingAddress,
    setIsShippingReadonly,
    formFieldsRef,
    getStateValue,
    emptyCart,
    orderAccount
  } = props;

  const navigate = useNavigate();
  const { setItemsInCart } = useHeaderMenu();
  const { hideBodyLoader } = useLoader();

  const handleSuccess = useCallback((result: any, pid?: number) => {
    const pageLocale = window.location.pathname.split("/")[1];
    let cartState = result?.cartItems?.cartItem;
    let shippingFlag = sessionStorage.getItem("isShipping");
    
    setBrand(result?.brand?.toUpperCase());
    
    // Handle tax exemption
    if (result.taxExemption && result.taxExemption.companyName && result.taxExemption.vatNo) {
      setFormTaxData({
        companyName: result.taxExemption.companyName,
        vatNo: result.taxExemption.vatNo,
      });
      setIsBusinessPurchase(true);
    }
    
    // Handle shipping flag
    if (shippingFlag && shippingFlag.length) {
      setIsShipping(JSON.parse(shippingFlag));
    }
    
    if (cartState && cartState.length > 0) {
      // Validate locale
      if (result?.locale) {
        const localeParts = result.locale.split('_');
        if (localeParts.length === 2) {
          const formattedResultLocale = `${localeParts[0]}-${localeParts[1].toLowerCase()}`;
          if (pageLocale !== formattedResultLocale) {
            hideBodyLoader();
            emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"));
            return;
          }
        }
      }

      // Build cart object for analytics
      const items = cartState.map((cartItem: any) => ({
        quantity: cartItem.quantity,
        price: {
          basePrice: parseFloat(
            cartItem.pricing.formattedListPrice.replace(currencySign, "")
          ),
          discount: 0,
        },
        productInfo: {
          sku: `sku-${cartItem.product.id}`,
          prodID: cartItem.product.id.toString(),
          productName: cartItem.product.name,
        },
      }));

      const formattedLocale = pageLocale
        .replace("-", "_")
        .toLowerCase()
        .replace(/^[a-z]{2}/, (match) => match.toUpperCase());
        
      const cart = {
        customerID: "",
        orderID: result ? result["cartId"] : "",
        currencyCode: result ? result["pricing"]["orderTotal"]["currency"] : "",
        loginState: nvCookieHelpers.getCookie("isUserLoggedIn") ? "loggedIn" : "guest",
        storeLocale: formattedLocale,
        promoCode: "no",
        saveCardOption: false,
        shippingMethod: result ? result["shippingMethod"]["description"] : "",
        items: items,
      };
      
      if (typeof window !== "undefined") {
        (window as any).CartOrder = cart;
      }
    } else {
      emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"));
    }
    
    console.log((window as any).CartOrder);
    
    if (result.totalItemsInCart) {
      let billing = result?.billingAddress;
      let shipping = result?.shippingAddress;
      setCartData(result);
      setItemsInCart(result.totalItemsInCart);

      // Navigate based on login status
      if (result.totalItemsInCart > 0) {
        if (nvCookieHelpers.getCookie("isUserLoggedIn")) {
          navigate(`/${pageLocale}/cart/user/loggedIn`);
        } else {
          navigate(`/${pageLocale}/cart/user/guest`);
        }
      } else {
        emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"));
      }

      // Set shipping options
      if (cartState && cartState.length > 0) {
        setShippingList(result.shippingOptions.shippingOption);
        setShippingOptions({
          shippingOption: result.shippingMethod.code
        });
      } else {
        if (nvCookieHelpers.getCookie("isUserLoggedIn")) {
          orderAccount();
        } else {
          emptyCart("", pid);
        }
      }
      
      // Handle logged-in user addresses
      if (nvCookieHelpers.getCookie("isUserLoggedIn")) {
        if (savedBillingAddress && !savedBillingAddress?.length) {
          const addresses = result["addresses"];
          console.log('Raw addresses from backend:', addresses);
          if (addresses?.length && !addresses.some((addr: any) => addr.isDefault)) {
            addresses[0].isDefault = true;
          }
          setSavedBillingAddress(addresses);
        }
        if (savedShippingAddress && !savedShippingAddress.length) {
          const addresses = result["addresses"];
          if (addresses?.length && !addresses.some((addr: any) => addr.isDefault)) {
            addresses[0].isDefault = true;
          }
          setSavedShippingAddress(addresses);
        }
        setFormBillingData((prevData: intrfce.iBillingFormData) => ({
          ...prevData,
          emailBilling: result?.emailAddress
            ? result?.emailAddress
            : result?.billingAddress[0]?.emailAddress || "",
        }));
      }

      // Handle billing address from cart
      if (result?.billingAddress) {
        const billingData = formFieldsRef.current.billing.data;
        const billingAddress = formFieldsRef.current.billing.address;
        
        setFormBillingData({
          ...(billing.id ? { id: Number(billing.id) } : {}),
          firstNameBilling: billing.firstName || billingData?.firstNameBilling,
          lastNameBilling: billing.lastName || billingData?.lastNameBilling,
          contactBilling: billing.phoneNumber || billingData?.contactBilling,
          emailBilling: billing.emailAddress || result?.emailAddress || billingData?.emailBilling,
            vemailBilling: billing.emailAddress || result?.emailAddress || billingData?.vemailBilling
        });
        
        setFormBillingAddress({
          pincode: billing.postalCode || billingAddress?.pincode,
          address: billing.line1 || billingAddress?.address,
          line2: billing.line2 || billingAddress?.line2 || '',
          cityValue: billing.city || billingAddress?.cityValue,
          stateValue: getStateValue(billing.countrySubdivision, billingAddress?.stateValue),
            county: countyField ? billing.county || billingAddress?.county : '',
          countryName: billing.countryName || billingAddress?.countryName,
          countryValue: billing.country || billingAddress?.countryValue,
        });
        
        setSelectedBillingAddress({
          ...(billing.id ? { id: Number(billing.id) } : {}),
          city: billing.city || billingAddress?.cityValue,
          countryName: billing.countryName || billingAddress?.countryName,
          country: billing.country || billingAddress?.countryValue,
          firstName: billing.firstName || billingData?.firstNameBilling,
          lastName: billing.lastName || billingData?.lastNameBilling,
          line1: billing.line1 || billingAddress?.address,
          line2: billing.line2 || billingAddress?.line2 || '',
          nickName: "",
          phoneNumber: billing.phoneNumber || billingData?.contactBilling,
          postalCode: billing.postalCode || billingAddress?.pincode,
          countrySubdivision: getStateValue(billing.countrySubdivision, billingAddress?.stateValue),
            county: countyField ? billing.county || billingAddress?.county : '',
        });
        
        if (Number(billing.id) > -1) {
          if (userType === "loggedIn" && savedBillingAddress.length) {
            setIsBillingReadonly(true);
          } else {
            setIsBillingReadonly(false);
          }
        }

        // Handle shipping address if different from billing
        if (result?.shippingAddress && shippingFlag && JSON.parse(shippingFlag)) {
          const shippingAddress = formFieldsRef.current.shipping.address;
          const shippingData = formFieldsRef.current.shipping.data;
          
          setFormData({
            ...(shipping.id ? { id: Number(shipping.id) } : {}),
            firstNameShiping: shipping?.firstName || shippingData?.firstNameShiping,
            lastNameShiping: shipping?.lastName || shippingData?.lastNameShiping,
            contactShiping: shipping?.phoneNumber || shippingData?.contactShiping,
            iBillingFormData: {
              ...(billing.id ? { id: Number(billing.id) } : {}),
              firstNameBilling: billing?.firstName || billingData?.firstNameBilling,
              lastNameBilling: billing?.lastName || billingData?.lastNameBilling,
              contactBilling: billing?.phoneNumber || billingData?.contactBilling,
              emailBilling: billing?.emailAddress || billingData?.emailBilling,
                vemailBilling: billing?.emailAddress || billingData?.vemailBilling
            },
            iShippingDetails: {
              shippingOption: result.shippingMethod.code
            },
            iBillingFormAddress: {
              pincode: billing?.postalCode || billingAddress?.pincode,
              address: billing?.line1 || billingAddress?.address,
              line2: billing?.line2 || billingAddress?.line2 || '',
              cityValue: billing?.city || billingAddress?.cityValue,
              stateValue: getStateValue(billing?.countrySubdivision, billingAddress?.stateValue),
                county: countyField ? billing?.county || billingAddress?.county : '',
              countryValue: billing?.country || billingAddress?.countryValue,
              countryName: billing?.countryName || billingAddress?.countryName,
            },
            iShippingFormAddress: {
              pincode: shipping.postalCode || shippingAddress?.pincode,
              address: shipping.line1 || shippingAddress?.address,
              line2: shipping.line2 || shippingAddress?.line2 || '',
              cityValue: shipping.city || shippingAddress?.cityValue,
              stateValue:
                locale === "en-us"
                  ? shipping.countrySubdivision || shippingAddress?.stateValue || "-"
                        : '',
              county:
                locale !== "en-us"
                  ? shipping.county || shippingAddress?.county || "-"
                        : '',
              countryValue: shipping.country || shippingAddress?.countryValue,
              countryName: shipping.countryName || shippingAddress?.countryName,
            },
          });
          
          setSelectedShippingAddress(
            JSON.parse(shippingFlag) ? shipping : billing
          );

          if (Number(shipping.id) > -1) {
            if (userType === "loggedIn" && savedShippingAddress.length) {
              setIsShippingReadonly(true);
            } else {
              setIsShippingReadonly(false);
            }
          }
        }
      }
      hideBodyLoader();
    } else {
      if (nvCookieHelpers.getCookie("isUserLoggedIn")) {
        orderAccount();
      } else {
        emptyCart("", pid);
      }
    }
  }, [
    currencySign,
    locale,
    countyField,
    userType,
    savedBillingAddress,
    savedShippingAddress,
    t,
    setBrand,
    setFormTaxData,
    setIsBusinessPurchase,
    setIsShipping,
    setCartData,
    setShippingList,
    setShippingOptions,
    setSavedBillingAddress,
    setSavedShippingAddress,
    setFormBillingData,
    setFormBillingAddress,
    setSelectedBillingAddress,
    setIsBillingReadonly,
    setFormData,
    setSelectedShippingAddress,
    setIsShippingReadonly,
    formFieldsRef,
    getStateValue,
    emptyCart,
    orderAccount,
    navigate,
    setItemsInCart,
    hideBodyLoader
  ]);

  return { handleSuccess };
};

